import React, { useState } from 'react';
import { Container, Grid } from '@mantine/core';
import { useParams } from 'react-router-dom';
import styles from './ModelDetail.module.scss';

// Components
import { LoadingState } from './components/LoadingState';
import { ErrorState } from './components/ErrorState';
import { ModelHeader } from './components/ModelHeader';
import { RiskOverviewCard } from './components/RiskOverviewCard';
import { ModelDetailsCard } from './components/ModelDetailsCard';
import { DatasetsTable } from './components/DatasetsTable';
import { PapersModal } from './components/PapersModal';

// Hooks
import { useModelDetail } from './hooks/useModelDetail';
import { FilesTable } from './components/FilesTable';
import { DependenciesTable } from './components/DependenciesTable';

const ModelDetail: React.FC = () => {
  const { modelId } = useParams<{ modelId: string }>();
  const [isPapersModalOpen, setIsPapersModalOpen] = useState(false);

  const { modelData, isLoading, isError } = useModelDetail(modelId);
  const hasAnalysisErrors =
    !isLoading &&
    modelData &&
    modelData?.analysisScanErrors &&
    modelData.analysisScanErrors.length > 0;

  if (isLoading) {
    return <LoadingState />;
  }

  if (isError || !modelData) {
    return <ErrorState />;
  }

  return (
    <Container size="xl" className={styles.container}>
      <ModelHeader modelData={modelData} />
      {hasAnalysisErrors && (
        <ErrorState
          minHeight={150}
          errorMessage={modelData?.analysisScanErrors?.join(', ')}
        />
      )}
      <Grid mb="md">
        <Grid.Col span={6}>
          <RiskOverviewCard modelData={modelData} />
        </Grid.Col>
        <Grid.Col span={6}>
          <ModelDetailsCard
            modelData={modelData}
            onShowPapers={() => setIsPapersModalOpen(true)}
          />
        </Grid.Col>
      </Grid>

      <DatasetsTable modelData={modelData} />
      <DependenciesTable modelData={modelData} />
      <FilesTable modelData={modelData} />

      <PapersModal
        opened={isPapersModalOpen}
        onClose={() => setIsPapersModalOpen(false)}
        papers={modelData.papers || []}
      />
    </Container>
  );
};

export default ModelDetail;
